import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { messages } from "./messages";
import { itemName as itemNameFor } from "./items";

/** Idiomas suportados; "pt" é a fonte (fallback de todas as chaves). */
export const LANGS = ["pt", "en"];
const STORAGE_KEY = "zrs_lang";
const NUMBER_LOCALE = { pt: "pt-BR", en: "en-US" };

/** Idioma atual fora do React (toasts em utils, exportações, etc.). */
let currentLang = "pt";

function detectLang() {
    try {
        const q = new URLSearchParams(window.location.search).get("lang");
        if (LANGS.includes(q)) return q;
    } catch {
        /* ignore */
    }
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (LANGS.includes(saved)) return saved;
    } catch {
        /* ignore */
    }
    const nav =
        (typeof navigator !== "undefined" && (navigator.languages?.[0] || navigator.language)) ||
        "pt";
    return String(nav).toLowerCase().startsWith("pt") ? "pt" : "en";
}

function interpolate(str, vars) {
    if (!vars || typeof str !== "string") return str;
    return str.replace(/\{(\w+)\}/g, (m, k) => (vars[k] != null ? String(vars[k]) : m));
}

/**
 * Tradução por chave plana ("nav.home"). Fallback: pt → a própria chave.
 * Os valores podem ser strings (com {vars}), arrays ou objetos.
 */
export function translate(key, vars, lang = currentLang) {
    const dict = messages[lang] || messages.pt;
    let v = dict[key];
    if (v === undefined) v = messages.pt[key];
    if (v === undefined) {
        if (process.env.NODE_ENV !== "production") console.warn("[i18n] chave em falta:", key);
        return key;
    }
    if (typeof v === "function") return v(vars || {});
    return interpolate(v, vars);
}

/** Escolhe a variante de um valor bilingue `{ pt, en }`; strings passam inalteradas. */
export function localize(value, lang = currentLang) {
    if (value && typeof value === "object" && !Array.isArray(value) && ("pt" in value || "en" in value)) {
        return value[lang] ?? value.pt ?? value.en;
    }
    return value;
}

export function getLang() {
    return currentLang;
}

export function formatNumber(n, lang = currentLang) {
    return Number(n).toLocaleString(NUMBER_LOCALE[lang] || "pt-BR");
}

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
    const [lang, setLangState] = useState(() => {
        currentLang = detectLang();
        return currentLang;
    });

    const setLang = useCallback((next) => {
        if (!LANGS.includes(next)) return;
        currentLang = next;
        setLangState(next);
        try {
            localStorage.setItem(STORAGE_KEY, next);
        } catch {
            /* ignore */
        }
        // ?lang= tem prioridade ao carregar; mantém-no coerente com a escolha feita no seletor.
        try {
            const url = new URL(window.location.href);
            if (url.searchParams.has("lang")) {
                url.searchParams.set("lang", next);
                window.history.replaceState(window.history.state, "", url);
            }
        } catch {
            /* ignore */
        }
    }, []);

    useEffect(() => {
        currentLang = lang;
        document.documentElement.lang = lang === "pt" ? "pt-BR" : "en";
        document.title = translate("meta.title", null, lang);
        const desc = document.querySelector('meta[name="description"]');
        if (desc) desc.setAttribute("content", translate("meta.description", null, lang));
    }, [lang]);

    const value = useMemo(
        () => ({
            lang,
            setLang,
            t: (key, vars) => translate(key, vars, lang),
            l: (v) => localize(v, lang),
            n: (num) => formatNumber(num, lang),
            item: (ptName) => itemNameFor(ptName, lang),
            numberLocale: NUMBER_LOCALE[lang],
        }),
        [lang, setLang],
    );

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** @returns {{ lang: "pt"|"en"; setLang: (l: string) => void; t: (key: string, vars?: object) => any; l: (v: any) => any; n: (num: number) => string; item: (ptName: string) => string; numberLocale: string }} */
export function useI18n() {
    const ctx = useContext(I18nContext);
    if (!ctx) throw new Error("useI18n fora de <I18nProvider>");
    return ctx;
}
